import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  type CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { env } from './env';

let userPool: CognitoUserPool | null = null;

function getUserPool(): CognitoUserPool {
  if (!userPool) {
    if (!env.cognito.userPoolId || !env.cognito.clientId) {
      throw new Error('Cognito not configured. Set VITE_COGNITO_USER_POOL_ID and VITE_COGNITO_CLIENT_ID.');
    }
    userPool = new CognitoUserPool({
      UserPoolId: env.cognito.userPoolId,
      ClientId: env.cognito.clientId,
    });
  }
  return userPool;
}

export interface AuthTokens {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}

/**
 * Discriminated result of `cognitoLogin`. When admins are created via
 * `AdminCreateUser` (or any user with a temp password), Cognito returns the
 * `NEW_PASSWORD_REQUIRED` challenge instead of issuing tokens. The caller must
 * collect a new password and call `complete()` to finish authentication.
 *
 * Forgot-password is NOT a valid path here — Cognito refuses to reset the
 * password of a user still in `FORCE_CHANGE_PASSWORD` state.
 */
export type LoginResult =
  | { kind: 'tokens'; tokens: AuthTokens }
  | { kind: 'newPasswordRequired'; complete: (newPassword: string) => Promise<AuthTokens> };

function tokensFromSession(session: CognitoUserSession): AuthTokens {
  return {
    idToken: session.getIdToken().getJwtToken(),
    accessToken: session.getAccessToken().getJwtToken(),
    refreshToken: session.getRefreshToken().getToken(),
    expiresAt: session.getIdToken().getExpiration() * 1000,
  };
}

export async function cognitoSignUp(params: {
  email: string;
  password: string;
  attributes: Record<string, string>;
}): Promise<{ userSub: string; needsConfirmation: boolean }> {
  return new Promise((resolve, reject) => {
    const attrs = Object.entries(params.attributes).map(
      ([Name, Value]) => new CognitoUserAttribute({ Name, Value }),
    );
    getUserPool().signUp(params.email, params.password, attrs, [], (err, result) => {
      if (err) return reject(err);
      if (!result) return reject(new Error('Empty signUp result'));
      resolve({ userSub: result.userSub, needsConfirmation: !result.userConfirmed });
    });
  });
}

export async function cognitoConfirm(email: string, code: string): Promise<void> {
  return new Promise((resolve, reject) => {
    new CognitoUser({ Username: email, Pool: getUserPool() }).confirmRegistration(code, true, (err) => {
      if (err) return reject(err);
      resolve();
    });
  });
}

export async function cognitoLogin(email: string, password: string): Promise<LoginResult> {
  return new Promise((resolve, reject) => {
    const user = new CognitoUser({ Username: email, Pool: getUserPool() });
    const auth = new AuthenticationDetails({ Username: email, Password: password });
    user.authenticateUser(auth, {
      onSuccess: (session: CognitoUserSession) => {
        resolve({ kind: 'tokens', tokens: tokensFromSession(session) });
      },
      onFailure: reject,
      newPasswordRequired: (userAttributes: Record<string, string>, requiredAttributes: string[]) => {
        // Build the values map only from attributes Cognito explicitly
        // requested via `requiredAttributes`. Passing anything else — even
        // mutable ones already set on the user (like `custom:role`) — makes
        // Cognito reject with "Cannot modify a non-required attribute".
        // For our pool `requiredAttributes` is always `[]`, so this is `{}`.
        const valuesMap: Record<string, string> = {};
        const stripImmutable = new Set(['email_verified', 'email']);
        for (const attr of requiredAttributes ?? []) {
          if (stripImmutable.has(attr)) continue;
          const v = userAttributes[attr];
          if (v != null) valuesMap[attr] = v;
        }
        resolve({
          kind: 'newPasswordRequired',
          complete: (newPassword: string) =>
            new Promise<AuthTokens>((res, rej) => {
              user.completeNewPasswordChallenge(newPassword, valuesMap, {
                onSuccess: (s) => res(tokensFromSession(s)),
                onFailure: rej,
              });
            }),
        });
      },
    });
  });
}

export async function cognitoForgotPassword(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    new CognitoUser({ Username: email, Pool: getUserPool() }).forgotPassword({
      onSuccess: () => resolve(),
      onFailure: reject,
    });
  });
}

export async function cognitoResetPassword(email: string, code: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    new CognitoUser({ Username: email, Pool: getUserPool() }).confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: reject,
    });
  });
}

export async function cognitoResendCode(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    new CognitoUser({ Username: email, Pool: getUserPool() }).resendConfirmationCode((err) => {
      if (err) return reject(err);
      resolve();
    });
  });
}

export function cognitoSignOut(): void {
  const u = getUserPool().getCurrentUser();
  u?.signOut();
}

export async function cognitoRefreshSession(): Promise<AuthTokens | null> {
  return new Promise((resolve) => {
    const u = getUserPool().getCurrentUser();
    if (!u) return resolve(null);
    u.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session) return resolve(null);
      resolve({
        idToken: session.getIdToken().getJwtToken(),
        accessToken: session.getAccessToken().getJwtToken(),
        refreshToken: session.getRefreshToken().getToken(),
        expiresAt: session.getIdToken().getExpiration() * 1000,
      });
    });
  });
}

export function mapCognitoErrorMessage(err: unknown): string {
  if (err instanceof Error) {
    const name = (err as Error & { code?: string }).code ?? err.name;
    switch (name) {
      case 'UsernameExistsException':
        return 'Esse email já está cadastrado.';
      case 'NotAuthorizedException':
        return 'Email ou senha incorretos.';
      case 'UserNotFoundException':
        return 'Usuário não encontrado.';
      case 'CodeMismatchException':
      case 'ExpiredCodeException':
        return 'Código inválido ou expirado.';
      case 'InvalidPasswordException':
        return 'Senha não atende aos requisitos.';
      case 'UserNotConfirmedException':
        return 'Você ainda não confirmou seu email. Verifique sua caixa de entrada.';
      case 'LimitExceededException':
      case 'TooManyRequestsException':
        return 'Muitas tentativas. Aguarde alguns minutos.';
      default:
        return err.message || 'Erro inesperado.';
    }
  }
  return 'Erro inesperado.';
}
